import { PROJECT_ICONS } from "@/constants/ProjectIcons";
import theme from "@/styles/theme";
import { HelpCenterRounded, SearchOff } from "@mui/icons-material";
import {
	Box,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	ListSubheader,
	Paper,
	Popover,
	TextField,
	Typography,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { type ChangeEvent, type MouseEvent, useState } from "react";
import { GUIDE_MAPPING, type GuideTheme, type GuideTopic } from "./Mapping";
import { CustomModal } from "../CustomModal";
import { useUserStore } from "@/store/useUserStore";
import type { ATTRIBUTION } from "@/constants/User";
import { CustomTooltip } from "../CustomTooltip";

type AtLeastOne<T> = [T, ...T[]];

type QuickGuide = {
	theme: (typeof GUIDE_MAPPING)[number]["theme"];
	topics: AtLeastOne<GuideTopic>;
};

function Item(topic: GuideTopic) {
	const [open, setOpen] = useState(false);

	const TopicContent = topic.content;

	return (
		<>
			<ListItemButton
				sx={{
					paddingInline: 1.5,
					paddingBlock: 0.4,
					borderRadius: "0.25rem",
				}}
				onClick={() => setOpen(true)}
			>
				<ListItemText
					slotProps={{
						root: { sx: { margin: 0 } },
						primary: {
							sx: {
								fontSize: "0.875rem",
								color: blueGrey[800],
							},
						},
					}}
				>
					{topic.title}
				</ListItemText>
			</ListItemButton>

			<CustomModal.Root
				open={open}
				close={() => setOpen(false)}
				maxWidth={"sm"}
			>
				<CustomModal.Header title={topic.title} subtitle={topic.subtitle} />
				<CustomModal.Body>
					<TopicContent />
				</CustomModal.Body>
			</CustomModal.Root>
		</>
	);
}

function getFilteredGuidesByAttributions({
	attributions,
	guides,
}: {
	attributions?: ATTRIBUTION[];
	guides: GuideTheme[];
}) {
	return guides
		.map(({ theme, topics }) => ({
			theme,
			topics: topics.filter((topic) => {
				if (!topic.roles || topic.roles.length === 0) {
					return true;
				}

				if (!attributions) {
					return false;
				}

				return topic.roles.some((role) => attributions.includes(role));
			}),
		}))
		.filter(({ topics }) => topics.length > 0) as QuickGuide[];
}

export function QuickGuides() {
	const attributions = useUserStore(({ attributions }) =>
		attributions?.map(({ name }) => name)
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const initialGuides = getFilteredGuidesByAttributions({
		guides: GUIDE_MAPPING,
		attributions,
	});

	const [guides, setGuides] = useState(initialGuides);

	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setGuides(
			getFilteredGuidesByAttributions({
				guides: GUIDE_MAPPING,
				attributions,
			})
		);
	};

	const handleSearch = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = event.target.value.trim().toLowerCase();

		if (!value) {
			setGuides(initialGuides);
			return;
		}

		const newResult = guides
			.map((guide) => ({
				...guide,
				topics: guide.topics.filter(({ title }) =>
					title.toLowerCase().includes(value)
				),
			}))
			.filter(({ topics }) => topics.length > 0) as QuickGuide[];

		setGuides(newResult);
	};

	const open = Boolean(anchorEl);
	const id = open ? "quick-guides-popover" : undefined;

	return (
		<>
			<CustomTooltip
				offsetX={0}
				offsetY={-14}
				placement='bottom-end'
				title={
					<Typography variant='body2' fontSize={"0.8rem"}>
						Guias rápidos
					</Typography>
				}
			>
				<IconButton aria-describedby={id} onClick={handleClick} color='inherit'>
					<HelpCenterRounded />
				</IconButton>
			</CustomTooltip>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<Paper
					sx={{
						paddingBlock: 1.5,
						width: 280,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						position: "relative",
					}}
				>
					<IconButton
						size='small'
						sx={{ position: "absolute", top: 8, right: 8 }}
						onClick={handleClose}
					>
						{<PROJECT_ICONS.ACTION.CLOSE fontSize='small' />}
					</IconButton>
					<Box sx={{ paddingInline: 2 }}>
						<Typography
							variant={"body2"}
							fontSize={"0.95rem"}
							color={theme.palette.raip.main}
						>
							Guias Rápidos
						</Typography>
						<Typography
							variant={"body2"}
							fontSize={"0.8rem"}
							color={blueGrey[500]}
						>
							Informações sobre a plataforma
						</Typography>
					</Box>
					<Box sx={{ paddingInline: 2 }}>
						<TextField
							fullWidth
							size='small'
							label='Busca'
							onChange={handleSearch}
						/>
					</Box>
					<Box sx={{ height: 220, overflow: "auto" }}>
						{guides.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									gap: 0.7,
									flex: 1,
									height: "80%",
									padding: 2,
								}}
							>
								<SearchOff fontSize='large' sx={{ color: blueGrey[400] }} />
								<Typography
									variant={"caption"}
									color={blueGrey[500]}
									sx={{ letterSpacing: 0.3, userSelect: "none" }}
								>
									Nenhum resultado
								</Typography>
							</Box>
						) : (
							guides.map((guide) => (
								<List
									key={guide.theme}
									sx={{ paddingInline: 1 }}
									component={"nav"}
									aria-labelledby={`quick-guide-${guide.theme}`}
									subheader={
										<ListSubheader
											component={"div"}
											id={`quick-guide-${guide.theme}`}
											sx={{
												padding: 0.5,
												lineHeight: "0.875rem",
												paddingInline: 1.5,
											}}
										>
											<Typography
												variant='caption'
												sx={{
													lineHeight: "0.875rem",
													textTransform: "uppercase",
													fontWeight: 600,
													color: blueGrey[400],
													pointerEvents: "none",
													userSelect: "none",
													"& .MuiListSubheader-gutters": {
														padding: 0,
													},
												}}
											>
												{guide.theme}
											</Typography>
										</ListSubheader>
									}
								>
									{guide.topics.map((topic) => (
										<Item key={topic.id} {...topic} />
									))}
								</List>
							))
						)}
					</Box>
				</Paper>
			</Popover>
		</>
	);
}
